import {BaseModel} from './BaseModel';
import {Request} from "./Request";
import {Utils} from "../common/Utils";
import {BaseClass} from "../base/BaseClass";

/**
 * The collection class represents a list of data entries from the query controller of the ARGO Rest API by
 * using the {@link Request} class.
 * Every data entry of the result list will be wrapped into a {@link Model} class.
 *
 * Notice: This is a general implementation. For using DataEntry's query controller
 * please use {@link DataList}
 */
export class BaseList extends BaseClass {

  constructor(options = {}) {

    super(options);
    /**
     * ARGO's collection type name
     * @type {String}
     * @private
     */
    this._collection = options.collection || 'dataEntries';
    /**
     * Model class for list entries
     * @type {BaseModel}
     * @private
     */
    this._model = options.model || BaseModel;
    /**
     * All model instances in the list.
     * @type {Array<BaseModel>}
     * @private
     */
    this._list = [];
    /**
     * List indexed by ID
     * @type {{}}
     * @private
     */
    this._listById = {};
    /**
     * Contains all referenced links send from the backend.
     * @type {Object}
     * @private
     */
    this._links = {};
    /**
     * Indicates the list is currently loading data from the backend.
     * @type {boolean}
     * @private
     */
    this._isLoading = false;
    /**
     * Current page from the part of the whole dataset. Starts at 1.
     * If page is 0, no result could be found.
     * @type {number}
     */
    this.page = 0;
    /**
     * Number of entries.
     * @type {number}
     */
    this.pageSize = 25;
    /**
     * Number of all entries for the query.
     * @type {number}
     */
    this.totalElements = 0;
    /**
     * Number of all pages
     * @type {number}
     */
    this.totalPages = 0;
  }

  /**
   * Creates a new Request instance.
   * @return {Request}
   * @private
   */
  _createRequest() {
    return this._req = new Request({collection: this._collection})
      .before(() => {
        this._isLoading = true;
        this.emit('sync:loading');
      });
  }

  /**
   * Creates a new model instance with given data.
   * @param data
   * @return {BaseModel}
   * @private
   */
  _createModelInstance(data) {
    let options = {
      data,
      collection: this._collection,
      listReference: this,
      fwd: {
        prefix: 'model',
        target: this
      },
      on: {
        destroy: this._removeInternal.bind(this)
      }
    };
    return new this._model(options);
  }

  /**
   * Fetches data from the ARGO backend by using the query controller
   * with the given filter.
   * @param options
   * @param {boolean} [options.silent] - prevent event emit
   * @param {boolean} [options.append] - append result of this query to the given list instead of resetting
   * @param {QueryOptions} [options.query] - options to be sent to backend's query controller
   * @return {Promise}
   */
  fetch(options = {}) {

    let silent = !!options.silent;
    this.query = options.query || {};

    this.triggerMethodOn('beforeFetch', [options]);

    return this._createRequest()
      .query(this.query)
      .finally(() => {
        this._isLoading = false;
      })
      .then((data) => {
        return this.parse(data, options);
      })
      .catch((err) => {
        if (!silent) {
          this.emit('sync:error', this, err, options);
        }
      });
  }

  /**
   * Reset the list instance, that means all models will be removed.
   * @param options
   * @param {boolean} [options.silent] - prevent event emit
   */
  reset(options = {}) {
    this.triggerMethodOn('beforeReset', [options]);

    this._list = [];
    this._listById = {};

    if (options.silent !== true) {
      this.emit('reset', this, options);
    }
  }

  /**
   * Parses the backend response and creates model instances for every
   * data entry.
   * @param data
   * @param options
   * @return {BaseList}
   */
  parse(data, options = {}) {

    this.triggerMethodOn('beforeParse', [data, options]);

    let newEntries = [];
    this.page = data.page.number;
    this.pageSize = data.page.size;
    this.totalElements = data.page.totalElements;
    this.totalPages = data.page.totalPages;
    this._links = data._links;

    // clear list
    if (options.append !== true) {
      this.reset({silent: true});
    }

    // convert all data entries into model instances
    if (data._embedded && data._embedded[this._collection] && data._embedded[this._collection].length) {
       newEntries = data._embedded[this._collection]
         .map((data) => this._createListItem(data));
    }

    this.emit('sync:done', this, newEntries, options);
    return this;
  }

  /**
   * Create a model instance from the given data object.
   * @param {Object} data
   * @return {BaseModel}
   * @private
   */
  _createListItem(data) {
    let model = this._createModelInstance(data);
    this._list.push(model);
    this._listById[model.getId()] = model;
    return model;
  }

  /**
   * Returns true if model is doing a request otherwise false.
   * @return {boolean}
   */
  isLoading() {
    return this._isLoading;
  }

  /**
   * Indicates a 'next' link is in the result, that means a proceeding
   * page exists.
   * @return {boolean}
   */
  hasNext() {
    return !!this._links.next;
  }

  /**
   * Send last request again with next page step.
   * @param options
   * @return {Promise}
   */
  next(options = {}) {

    options.query = this.query;
    options.query.page = (this.page || 0) + 1;

    return this.fetch(options);
  }

  /**
   * Indicates a 'prev' link is in the result, that means a previous
   * page exists.
   * @return {boolean}
   */
  hasPrev() {
    return !!this._links.prev;
  }

  /**
   * Send last request again but decreases the page number.
   * The page number cannot be lower than 0.
   * @param options
   * @return {Promise}
   */
  prev(options = {}) {

    options.query = this.query;
    options.query.page = (this.page > 0) ? this.page - 1 : 0;

    return this.fetch(options);
  }

  /**
   * Returns the whole list of all models.
   * @return {Array<BaseModel>}
   */
  getList() {
    return this._list;
  }

  /**
   * Returns current page number.
   * @return {number}
   */
  getPage() {
    return this.page;
  }

  /**
   * Returns number of entries per page coming back from the backend.
   * @return {number}
   */
  getPageSize() {
    return this.pageSize;
  }

  /**
   * Returns number of all pages.
   * @return {number}
   */
  getTotalPages() {
    return this.totalPages;
  }

  /**
   * Returns number of all entries for the given query.
   * @return {number}
   */
  getTotalSize() {
    return this.totalElements;
  }

  /**
   * Returns true if the list is empty.
   * @return {boolean}
   */
  isEmpty() {
    return this.size() === 0;
  }

  /**
   * Returns the number of models in the list.
   * @return {number}
   */
  size() {
    return this._list.length;
  }

  /**
   * Call native forEach on the list.
   * @param {Function} cb - callback function
   */
  forEach(cb) {
    this._list.forEach(cb);
  }

  /**
   * Call native map on the list.
   * @param {Function} cb - callback function
   */
  map(cb) {
    return this._list.map(cb);
  }

  /**
   * Returns the model with the given system id or null if not found.
   * @param {number} id - system id of the data set
   * @return {BaseModel|null}
   */
  findById(id) {
    if (this._listById.hasOwnProperty(id)) {
      return this._listById[id];
    }
    return null;
  }

  /**
   * Given argument search can be a predicate function which returns true or false
   * or must be a key-value-object.
   * This functions returns the first model where the given predicate function (search)
   * returns true or all key-value matches against model's properties.
   * @param {Object|Function} search -
   * @return {BaseModel|null}
   */
  find(search) {

    let cb = search;
    if (!Utils.isFunction(search)) {

      // property value map -> check against model properties
      if ($.isPlainObject(cb)) {
        let searchMap = search;
        cb = (model) => {
          for (let key in searchMap) {
            if (searchMap[key] !== model.get(key)) {
              return false;
            }
          }
          return true;
        };
      }
      else {
        // other types not supported
        return null;
      }
    }

    return this._list.find(cb)
  }

  /**
   * Creates a new model instance with the given plain entry data
   * and add it to the list.
   * @param {Object} entry - key value map passed to the new model instance
   * @return {BaseModel}
   */
  createModel(entry) {
    let model = this._createModelInstance(entry);
    this.add(model);
    return model;
  }

  /**
   * Adds the given model to the end of the list.
   * @param {BaseModel} model - new model instance to be added to the list
   * @param options
   * @return {BaseList}
   */
  add(model, options = {}) {
    if (model instanceof this._model) {
      this._list.push(model);
      this._listById[model.getId()] = model;
      this.invoke('_add', [model, options]);

      if (options.silent !== true) {
        this.emit('add', this, model, options);
      }
    }
    return this;
  }

  /**
   * Remove the given model from the list. The model instance must
   * exists in the list.
   * @param {BaseModel} model - model instance to be removed
   * @param {Object} options
   * @return {BaseList}
   */
  remove(model, options = {}) {
    if (model instanceof BaseModel) {

      let index = this._removeInternal(model);
      if (index === -1) {
        return false;
      }

      if (options.silent !== true) {
        this.emit('remove', this, model, index, options);
      }
    }
    return this;
  }

  /**
   *
   * @param {BaseModel} model
   * @return {number}
   * @private
   */
  _removeInternal(model) {
    let index = this._list.findIndex((it) => it === model);
    if (index > -1) {
      delete this._listById[model.getId()];
      this._list.splice(index, 1);
      this.invoke('_remove', [model]);
    }
    return index;
  }

}